import { Box, Button, Checkbox, Chip, Container, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { makeGetRequest, makePostRequest } from 'utils/api'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import BusinessIcon from '@mui/icons-material/Business'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import GestureIcon from '@mui/icons-material/Gesture'
import CancelIcon from '@mui/icons-material/Cancel'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import { toast } from 'react-toastify'
import CreateEvent from './CreateEvent'
import CreateTask from './CreateTask'

const C = {
  panel: '#0c1e3a',
  card: '#102a4d',
  cardActive: '#16386b',
  border: '1px solid rgba(193,218,241,0.12)',
  borderActive: '1px solid #1b75d0',
  accent: 'linear-gradient(135deg,#1b75d0 0%,#196081 100%)',
  textHi: '#eaf2fb',
  textLo: '#93a7c4',
}

// Métadonnées de statut : couleur + libellé d'affichage (la valeur brute reste
// celle stockée en base, utilisée par l'API jobs-status-update).
const STATUS_META = {
  Enregistré: { color: '#e5aa20', label: 'Enregistré' },
  'Candidature envoyé': { color: '#1b75d0', label: 'Candidature envoyée' },
  Accepter: { color: '#2e9e5b', label: 'Accepté' },
  Signer: { color: '#7c5cff', label: 'Signé' },
  Refuser: { color: '#e5484d', label: 'Refusé' },
}

const PIPELINE = ['Enregistré', 'Candidature envoyé', 'Accepter', 'Signer', 'Refuser']

function StatusChip({ status }) {
  const meta = STATUS_META[status] || { color: C.textLo, label: status }
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{
        background: `${meta.color}22`,
        color: meta.color,
        fontWeight: 700,
        border: `1px solid ${meta.color}55`,
      }}
    />
  )
}

export default function MyOffer() {
  const [operations, setOperations] = useState({ results: [], count: 0 })
  const [selectedRows, setSelectedRows] = useState([])
  const [createTache, setCreateTache] = useState(false)
  const [createEvent, setCreateEvent] = useState(false)
  const [selectedJob, setSelectedJob] = useState('')

  useEffect(() => {
    fetchOperations()
  }, [])

  const fetchOperations = async () => {
    const response = await makeGetRequest('/api/Professional/get-offres/', {}, true)
    if (response.status === 200) {
      setOperations({ results: response.data, count: response.data.length })
      setSelectedRows([])
    }
  }

  const counts = useMemo(() => {
    const c = {}
    ;(operations.results || []).forEach((r) => {
      c[r.status] = (c[r.status] || 0) + 1
    })
    return c
  }, [operations])

  const handleDelete = async () => {
    if (selectedRows.length === 0) return toast.warn('Sélectionne au moins une offre.', { theme: 'dark' })
    await makePostRequest('/api/Professional/delete-offeres/', { ids: selectedRows }, {}, true)
    fetchOperations()
  }
  const handelStatus = async (status) => {
    if (selectedRows.length === 0) return toast.warn('Sélectionne au moins une offre.', { theme: 'dark' })
    await makePostRequest('/api/Professional/jobs-status-update/', { ids: selectedRows, status }, {}, true)
    fetchOperations()
  }

  const toggleRow = (id) =>
    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const toggleModal = () => setCreateEvent(false)
  const rows = operations.results || []

  return (
    <Container maxWidth="xl" disableGutters>
      {createEvent && <CreateEvent toggleModal={toggleModal} title={selectedJob} />}
      {createTache && <CreateTask setCreateTache={setCreateTache} title={selectedJob} />}

      {/* En-tête + pipeline de statuts */}
      <Box sx={{ background: C.panel, border: C.border, borderRadius: 3, p: 2.5, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: C.textHi }}>
            Mes offres d'emploi <span style={{ color: C.textLo, fontWeight: 600 }}>({operations.count})</span>
          </Typography>
        </Stack>
        <Stack direction="row" flexWrap="wrap" gap={1.5} mt={2}>
          {PIPELINE.map((s) => {
            const meta = STATUS_META[s]
            return (
              <Box
                key={s}
                sx={{
                  flex: '1 1 140px',
                  minWidth: 120,
                  background: C.card,
                  border: `1px solid ${meta.color}33`,
                  borderRadius: 2,
                  p: 1.5,
                }}
              >
                <Typography sx={{ fontSize: 26, fontWeight: 800, color: meta.color, lineHeight: 1 }}>
                  {counts[s] || 0}
                </Typography>
                <Typography sx={{ fontSize: 12, color: C.textLo, mt: 0.5 }}>{meta.label}</Typography>
              </Box>
            )
          })}
        </Stack>
      </Box>

      {/* Barre d'actions */}
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={1.5}
        alignItems="center"
        sx={{ background: C.panel, border: C.border, borderRadius: 3, p: 1.5, mb: 2 }}
      >
        <Typography sx={{ fontSize: 13, color: C.textLo, mr: 1 }}>
          {selectedRows.length} sélectionnée(s)
        </Typography>
        <WorkflowButton color="#2e9e5b" icon={<CheckCircleIcon />} onClick={() => handelStatus('Accepter')}>
          Accepter
        </WorkflowButton>
        <WorkflowButton color="#7c5cff" icon={<GestureIcon />} onClick={() => handelStatus('Signer')}>
          Signer
        </WorkflowButton>
        <WorkflowButton color="#e5484d" icon={<CancelIcon />} onClick={() => handelStatus('Refuser')}>
          Refuser
        </WorkflowButton>
        <WorkflowButton color="#8794a8" icon={<DeleteOutlineIcon />} onClick={handleDelete}>
          Supprimer
        </WorkflowButton>
      </Stack>

      {/* Liste des offres suivies */}
      {rows.length > 0 ? (
        <Stack spacing={1.25}>
          {rows.map((row) => {
            const offre = row.offre || {}
            const selected = selectedRows.includes(row.id)
            return (
              <Box
                key={row.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  background: selected ? C.cardActive : C.card,
                  border: selected ? C.borderActive : C.border,
                  borderRadius: 2.5,
                  p: 1.5,
                  transition: 'all .15s ease',
                }}
              >
                <Checkbox
                  checked={selected}
                  onChange={() => toggleRow(row.id)}
                  sx={{ color: C.textLo, '&.Mui-checked': { color: '#74b7fd' } }}
                />
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    background: C.accent,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <BusinessIcon sx={{ color: 'white' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: C.textHi,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {offre.title}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#c1daf1' }}>
                    {offre.company_name}
                    {offre.salary_range ? ` · ${offre.salary_range}` : ''}
                    {offre.contract_type ? ` · ${offre.contract_type}` : ''}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: C.textLo, mt: 0.25 }}>
                    Candidature : {row.applied_date ? moment(row.applied_date).format('DD MMM YYYY') : '—'}
                  </Typography>
                </Box>
                <StatusChip status={row.status} />
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Créer une tâche">
                    <IconButton
                      onClick={() => {
                        setSelectedJob(offre.title)
                        setCreateTache(true)
                      }}
                      sx={{ color: C.textLo, '&:hover': { color: '#74b7fd' } }}
                    >
                      <PlaylistAddCheckIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Créer un événement">
                    <IconButton
                      onClick={() => {
                        setSelectedJob(offre.title)
                        setCreateEvent(true)
                      }}
                      sx={{ color: C.textLo, '&:hover': { color: '#74b7fd' } }}
                    >
                      <CalendarMonthIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            )
          })}
        </Stack>
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={1.5}
          sx={{ background: C.panel, border: C.border, borderRadius: 3, py: 8, color: C.textLo }}
        >
          <WorkOutlineIcon sx={{ fontSize: 64, opacity: 0.4 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Aucune offre suivie pour l'instant</Typography>
          <Typography sx={{ fontSize: 13, opacity: 0.8, textAlign: 'center', maxWidth: 420 }}>
            Enregistre ou postule à une offre depuis l'onglet « Offre d'emploi » pour la retrouver
            ici et suivre sa progression.
          </Typography>
        </Stack>
      )}
    </Container>
  )
}

function WorkflowButton({ color, icon, onClick, children }) {
  return (
    <Button
      onClick={onClick}
      startIcon={icon}
      variant="outlined"
      sx={{
        color,
        borderColor: `${color}66`,
        textTransform: 'none',
        fontWeight: 700,
        '&:hover': { borderColor: color, background: `${color}14` },
      }}
    >
      {children}
    </Button>
  )
}
